// Pure single/double elimination engine for tournament bracket stages.
//
// Only the seed order (`participants`) and the entered results are stored in
// Firestore. The match structure is rebuilt from the participant count, and
// who plays whom is derived by replaying results — so a bracket can never be
// half-updated, and undoing a result is just deleting it.

// A slot that will never hold a player (a bye). `null` means "not decided yet".
export const EMPTY = '__empty__';

export const MAX_BRACKET_PLAYERS = 64;

export const bracketSize = (count) => {
  let size = 2;
  while (size < count) size *= 2;
  return size;
};

// Standard seeding: 1 and 2 can only meet in the final. seedOrder(8) =
// [1, 8, 4, 5, 2, 7, 3, 6]; round-one match i pairs entries 2i and 2i + 1.
export const seedOrder = (size) => {
  let order = [1];
  while (order.length < size) {
    const sum = order.length * 2 + 1;
    order = order.flatMap((seed) => [seed, sum - seed]);
  }
  return order;
};

const matchId = (side, round, index) => `${side}${round}-${index}`;

// Which upper-round match's loser drops into lower-bracket match `i` of the
// k-th drop-in round. Alternating the order keeps players who just met apart
// (standard Challonge / start.gg behaviour).
const dropIndex = (k, i, count) => {
  if (count === 1) return 1;
  if (k % 2 === 1) return count + 1 - i;
  return ((i - 1 + count / 2) % count) + 1;
};

// Match list in dependency order: every match comes after the matches it
// takes players from.
export const buildBracket = (count, { elimination = 'single', grandFinalReset = true } = {}) => {
  const size = bracketSize(count);
  const rounds = Math.log2(size);
  const double = elimination === 'double' && size >= 4;
  const order = seedOrder(size);
  const matches = [];

  for (let r = 1; r <= rounds; r += 1) {
    for (let i = 1; i <= size / 2 ** r; i += 1) {
      const sources =
        r === 1
          ? [{ seed: order[2 * i - 2] }, { seed: order[2 * i - 1] }]
          : [
              { match: matchId('W', r - 1, 2 * i - 1), take: 'winner' },
              { match: matchId('W', r - 1, 2 * i), take: 'winner' },
            ];
      matches.push({
        id: matchId('W', r, i),
        side: 'W',
        round: r,
        index: i,
        sources,
      });
    }
  }

  const lowerRounds = double ? 2 * rounds - 2 : 0;
  for (let r = 1; r <= lowerRounds; r += 1) {
    const n = size / 2 ** (Math.ceil(r / 2) + 1);
    for (let i = 1; i <= n; i += 1) {
      let sources;
      if (r === 1) {
        sources = [
          { match: matchId('W', 1, 2 * i - 1), take: 'loser' },
          { match: matchId('W', 1, 2 * i), take: 'loser' },
        ];
      } else if (r % 2 === 0) {
        const k = r / 2;
        sources = [
          { match: matchId('L', r - 1, i), take: 'winner' },
          { match: matchId('W', k + 1, dropIndex(k, i, n)), take: 'loser' },
        ];
      } else {
        sources = [
          { match: matchId('L', r - 1, 2 * i - 1), take: 'winner' },
          { match: matchId('L', r - 1, 2 * i), take: 'winner' },
        ];
      }
      matches.push({
        id: matchId('L', r, i),
        side: 'L',
        round: r,
        index: i,
        sources,
      });
    }
  }

  if (double) {
    matches.push({
      id: 'GF1',
      side: 'GF',
      round: 1,
      index: 1,
      sources: [
        { match: matchId('W', rounds, 1), take: 'winner' },
        { match: matchId('L', lowerRounds, 1), take: 'winner' },
      ],
    });
    if (grandFinalReset) {
      // Only played when the lower-bracket player wins GF1 (see resolveBracket).
      matches.push({
        id: 'GF2',
        side: 'GF',
        round: 2,
        index: 1,
        sources: [
          { match: 'GF1', take: 'slot0' },
          { match: 'GF1', take: 'slot1' },
        ],
      });
    }
  }

  return {
    size,
    rounds,
    lowerRounds,
    elimination: double ? 'double' : 'single',
    grandFinalReset: double && grandFinalReset,
    matches,
  };
};

// Replays `results` over the structure. Returns every match with its players,
// status ('pending' | 'ready' | 'done' | 'bye' | 'skipped'), winner and loser,
// plus the champion and final placements (1 = champion, ties share a rank).
export const resolveBracket = (structure, participants = [], results = {}) => {
  const byId = {};
  const resolved = structure.matches.map((m) => {
    const players = m.sources.map((src) => {
      if (src.seed) return participants[src.seed - 1] || EMPTY;
      const from = byId[src.match];
      if (src.take === 'slot0' || src.take === 'slot1') {
        return from.status === 'done' ? from.players[src.take === 'slot0' ? 0 : 1] : null;
      }
      return from[src.take];
    });
    let status;
    let winner = null;
    let loser = null;
    const result = results[m.id];
    const score = [result?.scoreA ?? null, result?.scoreB ?? null];

    if (m.id === 'GF2' && byId.GF1.status === 'done' && byId.GF1.winner === byId.GF1.players[0]) {
      status = 'skipped';
    } else if (players.includes(null)) {
      status = 'pending';
    } else if (players.includes(EMPTY)) {
      status = 'bye';
      winner = players[0] === EMPTY ? players[1] : players[0];
      loser = EMPTY;
    } else if (result && players.includes(result.winner)) {
      status = 'done';
      winner = result.winner;
      loser = players[0] === winner ? players[1] : players[0];
    } else {
      status = 'ready';
    }
    const out = { ...m, players, status, winner, loser, score };
    byId[m.id] = out;
    return out;
  });

  const double = structure.elimination === 'double';
  let champion = null;
  if (!double) {
    champion = byId[matchId('W', structure.rounds, 1)].winner;
  } else if (byId.GF1.status === 'done') {
    const lowerWon = byId.GF1.winner === byId.GF1.players[1];
    champion = lowerWon && byId.GF2 ? byId.GF2.winner : byId.GF1.winner;
  }
  if (champion === EMPTY) champion = null;

  // A player's placement is decided by the round they were knocked out in:
  // later knock-out = better. Upper-bracket losses don't eliminate in double
  // elimination, and the upper-bracket player losing GF1 goes to GF2.
  const knockedOut = {};
  resolved.forEach((m) => {
    if (m.status !== 'done') return;
    if (double && m.side === 'W') return;
    if (m.id === 'GF1' && byId.GF2 && m.loser === m.players[0]) return;
    if (m.side === 'GF') knockedOut[m.loser] = structure.lowerRounds + 1;
    else knockedOut[m.loser] = m.round;
  });
  const placements = {};
  if (champion) placements[champion] = 1;
  const keys = Object.values(knockedOut);
  Object.entries(knockedOut).forEach(([playerId, key]) => {
    placements[playerId] = 1 + (champion ? 1 : 0) + keys.filter((k) => k > key).length;
  });

  return {
    matches: resolved,
    byId,
    champion,
    placements,
    complete: !!champion,
  };
};

// Every match that (transitively) takes a player from `id`.
const dependents = (structure, id) => {
  const out = new Set();
  const visit = (from) => {
    structure.matches.forEach((m) => {
      if (!out.has(m.id) && m.sources.some((s) => s.match === from)) {
        out.add(m.id);
        visit(m.id);
      }
    });
  };
  visit(id);
  return [...out];
};

// Later matches whose stored result would be wiped by changing `id`.
export const resultsToReset = (structure, results, id) =>
  dependents(structure, id).filter((d) => results[d]);

// Sets (or clears, with `result = null`) one match result. If the outcome
// changes, every later result that depended on it is cleared too, so the
// bracket never shows a match that could not have happened.
export const applyBracketResult = (structure, participants, results, id, result) => {
  const current = resolveBracket(structure, participants, results).byId[id];
  if (!current) throw new Error('Unknown match.');
  if (current.status !== 'ready' && current.status !== 'done') {
    throw new Error('This match is not ready to be played.');
  }
  if (result && !current.players.includes(result.winner)) {
    throw new Error('The winner must be one of the two players.');
  }
  const next = { ...results };
  if (result) {
    next[id] = {
      winner: result.winner,
      scoreA: Number.isFinite(result.scoreA) ? result.scoreA : null,
      scoreB: Number.isFinite(result.scoreB) ? result.scoreB : null,
    };
  } else {
    delete next[id];
  }
  const outcomeChanged = (result?.winner ?? null) !== current.winner;
  const reset = outcomeChanged ? resultsToReset(structure, results, id) : [];
  reset.forEach((d) => delete next[d]);
  return { results: next, reset };
};

export const roundLabel = (structure, side, round) => {
  const double = structure.elimination === 'double';
  if (side === 'GF') return round === 1 ? 'Grand final' : 'Grand final reset';
  if (side === 'L') {
    return round === structure.lowerRounds ? 'Lower final' : `Lower round ${round}`;
  }
  const prefix = double ? 'Upper ' : '';
  const fromEnd = structure.rounds - round;
  if (fromEnd === 0) return double ? 'Upper final' : 'Final';
  if (fromEnd === 1) return `${prefix}semifinals`.replace(/^./, (c) => c.toUpperCase());
  if (fromEnd === 2) return `${prefix}quarterfinals`.replace(/^./, (c) => c.toUpperCase());
  return `${prefix}round ${round}`.replace(/^./, (c) => c.toUpperCase());
};
